<template>
  <div
    class="licenses-card"
    :class="loadingContent ? 'loading large' : ''"
  >
    <h1 class="title">
      Pacientes
    </h1>

    <!-- Resumo das licenças -->
    <p class="licenses-summary">
      Licenças disponíveis:
      <lila-license-badge :licenses="licenses" />
      <span class="licenses-dot">·</span>
      Precisa de mais licenças?
      <a
        class="licenses-contact"
        href="#"
        @click.prevent="contactSales"
      >Entre em contato!</a>
    </p>

    <lila-message :alert-data="alert" />

    <!-- Filtros -->
    <div
      v-show="!loadingContent"
      class="licenses-filters"
    >
      <div class="licenses-filter licenses-filter--search">
        <label class="licenses-filter-label">
          Nome ou Nº de Matrícula do Paciente
        </label>
        <form @submit.prevent="applySearch">
          <lila-input
            v-model="filters.search"
            type="inputSearch"
            class="with-shadow"
            placeholder="Pesquisar"
            :btn-clear-search="filters.searchActive"
            @custom-clear-search-input-event="clearSearch"
          />
        </form>
      </div>

      <div class="licenses-filter">
        <label class="licenses-filter-label">Equipe</label>
        <div class="licenses-filter-picker-wrap">
          <button
            type="button"
            class="licenses-filter-select"
            :class="{ 'has-clear': filters.team }"
            @click.stop="filterPickerOpen = !filterPickerOpen"
          >
            <span :class="{ 'is-placeholder': !filters.team }">
              {{ filters.team || "Selecione" }}
            </span>
            <span
              v-if="!filters.team"
              class="licenses-filter-caret"
            />
          </button>
          <button
            v-if="filters.team"
            type="button"
            class="licenses-filter-clear"
            aria-label="Limpar filtro de equipe"
            @click.stop="applyTeamFilter(null)"
          >
            &times;
          </button>
          <div
            v-if="filterPickerOpen"
            class="licenses-filter-picker"
          >
            <team-tree-picker
              :tree="teamTree"
              :immediate="true"
              :units-selectable="false"
              :current-id="filters.teamId"
              :show-current="false"
              all-label="Todas as equipes"
              title="Filtrar por equipe"
              @select="applyTeamFilter"
              @cancel="filterPickerOpen = false"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Barra de ação em lote (bulk-edit) -->
    <div
      v-if="!loadingContent && selectedIds.length > 0"
      class="licenses-bulk-bar"
    >
      <span class="licenses-bulk-count">
        {{ selectedIds.length }}
        {{ selectedIds.length === 1 ? "paciente selecionado" : "pacientes selecionados" }}
      </span>

      <div class="licenses-bulk-actions">
        <button
          type="button"
          class="licenses-bulk-apply"
          :disabled="bulkSaving"
          @click="applyBulkLicense(true)"
        >
          {{ bulkSaving ? "Aplicando…" : "Licenciar" }}
        </button>
        <button
          type="button"
          class="licenses-bulk-apply licenses-bulk-apply--outline"
          :disabled="bulkSaving"
          @click="applyBulkLicense(false)"
        >
          Remover licença
        </button>
      </div>

      <button
        type="button"
        class="licenses-bulk-clear"
        @click="clearSelection"
      >
        Limpar seleção
      </button>
    </div>

    <!-- Tabela -->
    <div
      v-show="!loadingContent"
      class="licenses-table-wrapper custom-scroll"
    >
      <table class="licenses-table">
        <thead>
          <tr>
            <th class="has-text-centered licenses-check-col">
              <label class="licenses-check">
                <input
                  type="checkbox"
                  :checked="allPageSelected"
                  :indeterminate.prop="somePageSelected && !allPageSelected"
                  @change="toggleSelectAllPage($event)"
                >
              </label>
            </th>
            <th>Nome do Paciente</th>
            <th>Sexo</th>
            <th>Idade</th>
            <th>CID</th>
            <th>Equipe</th>
            <th class="has-text-centered">
              Licenciado
            </th>
            <th class="has-text-centered">
              Editar
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
            :class="{ 'licenses-row--selected': isSelected(row.id) }"
          >
            <td class="has-text-centered licenses-check-col">
              <label class="licenses-check">
                <input
                  type="checkbox"
                  :checked="isSelected(row.id)"
                  @change="toggleSelect(row)"
                >
              </label>
            </td>
            <td class="licenses-name">
              {{ row.name }}
            </td>
            <td>{{ row.gender }}</td>
            <td>{{ row.age }}</td>
            <td>{{ row.cid }}</td>
            <td>{{ row.team || '—' }}</td>
            <td class="has-text-centered">
              <label class="licenses-switch">
                <input
                  type="checkbox"
                  :checked="row.isLicensed"
                  :disabled="row.updatingLicense"
                  @change="toggleLicense(row, $event)"
                >
                <span class="licenses-slider" />
              </label>
            </td>
            <td class="has-text-centered">
              <button
                type="button"
                class="licenses-edit-btn"
                title="Editar"
                @click="editRow(row)"
              >
                <lila-svg-icon :src="require('@assets/icons/icon_editar.svg')" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <p
        v-if="!loadingContent && filteredRows.length === 0"
        class="licenses-empty has-text-centered"
      >
        Nenhum paciente encontrado.
      </p>
    </div>

    <lila-pagination
      v-show="!loadingContent && filteredRows.length > 0"
      :pagination-config="pagination"
      @custom-change-page-event="changePage"
    />
  </div>
</template>

<script>
import Input from "@components/shared/fields/Input.vue";
import SvgIcon from "@components/shared/images/SvgIcon.vue";
import Message from "@components/shared/message/Message.vue";
import Pagination from "@components/shared/pagination/Pagination.vue";
import TeamTreePicker from "@components/shared/lila/TeamTreePicker.vue";
import LicenseBadge from "@components/shared/lila/LicenseBadge.vue";

export default {
  components: {
    "lila-input": Input,
    "lila-svg-icon": SvgIcon,
    "lila-message": Message,
    "lila-pagination": Pagination,
    "team-tree-picker": TeamTreePicker,
    "lila-license-badge": LicenseBadge
  },
  data() {
    return {
      loadingContent: false,
      alert: {
        show: false,
        type: "success",
        message: ""
      },
      patients: [],
      teams: [],
      // Equipes visíveis (api/team/available) para o dropdown de filtro em árvore.
      transferTargets: [],
      filterPickerOpen: false,
      // Bulk-edit: ids selecionados + estado da ação em lote.
      selectedIds: [],
      bulkSaving: false,
      filters: {
        search: "",
        searchActive: false,
        team: null,
        teamId: null
      },
      pagination: {
        current: 1,
        totalPages: 0,
        perPage: 10
      },
      // Resumo das licenças.
      // TODO: substituir pelos valores reais vindos do backend (plano/assinatura).
      // O `available` é atualizado de forma otimista ao alternar o toggle "Licenciado".
      licenses: {
        available: 0,
        total: 0,
        // Instituição sem teto de licenças (GET /api/Licenses → { unlimited: true }).
        unlimited: false
      }
    };
  },
  computed: {
    // Árvore Unidades › Equipes a partir do api/team/available (com Path).
    teamTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    },
    filteredRows() {
      const term = this.filters.search.trim().toLowerCase();

      return this.patients.filter((row) => {
        const matchesSearch =
          !term ||
          row.name.toLowerCase().includes(term) ||
          (row.registerNumber || "").toLowerCase().includes(term);
        const matchesTeam = !this.filters.team || row.team === this.filters.team;
        return matchesSearch && matchesTeam;
      });
    },
    pagedRows() {
      const start = (this.pagination.current - 1) * this.pagination.perPage;
      return this.filteredRows.slice(start, start + this.pagination.perPage);
    },
    allPageSelected() {
      return (
        this.pagedRows.length > 0 &&
        this.pagedRows.every((row) => this.selectedIds.includes(row.id))
      );
    },
    somePageSelected() {
      return this.pagedRows.some((row) => this.selectedIds.includes(row.id));
    }
  },
  watch: {
    filteredRows() {
      this.recalculatePagination();
    }
  },
  async created() {
    // Carrega as equipes antes dos pacientes para conseguir resolver o nome
    // da equipe a partir do TeamId retornado na listagem.
    await this.getTeams();
    this.getTransferTargets();
    this.getPatients();
    this.getLicenses();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    // Equipes visíveis (api/team/available, escopadas, só IsTeam=1) com Path para o filtro.
    async getTransferTargets() {
      try {
        const res = await this.$axios.get("api/team/available", {
          headers: this.authHeader()
        });
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.transferTargets = list
          .filter((item) => item && this.$Utilities.getValueIgnoreCase(item, "IsTeam"))
          .map((item) => ({
            id: this.$Utilities.getValueIgnoreCase(item, "Id"),
            name: this.$Utilities.getValueIgnoreCase(item, "Name"),
            path: (this.$Utilities.getValueIgnoreCase(item, "Path") || []).map(
              (unit) => ({
                id: this.$Utilities.getValueIgnoreCase(unit, "Id"),
                name: this.$Utilities.getValueIgnoreCase(unit, "Name")
              })
            )
          }));
      } catch (error) {
        console.error("Erro ao carregar equipes do filtro:", error);
      }
    },

    // Filtro por equipe (picker immediate → id): guarda o nome em filters.team.
    applyTeamFilter(id) {
      if (id == null) {
        this.filters.team = null;
        this.filters.teamId = null;
      } else {
        const t = this.$Utilities.findTeamOrUnit(this.transferTargets, this.teams, id);
        this.filters.team = t ? t.name : null;
        this.filters.teamId = t ? id : null;
      }
      this.filterPickerOpen = false;
      this.applyClientFilters();
    },

    handleClickOutside(event) {
      if (!this.filterPickerOpen) return;
      const picker = document.querySelector(".licenses-filter-picker");
      if (picker && !picker.contains(event.target)) {
        this.filterPickerOpen = false;
      }
    },

    async getTeams() {
      try {
        const res = await this.$axios.get("api/team", {
          headers: this.authHeader()
        });
        this.teams = (res.data || []).map((item) => ({
          id: item.Id,
          name: item.Name
        }));
      } catch (error) {
        console.error("Erro ao carregar equipes:", error);
      }
    },

    async getPatients() {
      this.loadingContent = true;
      try {
        const res = await this.$axios.get("/api/patient?page=1&limit=1000", {
          headers: this.authHeader()
        });
        const list = (res.data && res.data.Data) || [];
        this.patients = list
          .filter((item) => item != null)
          .map((item) => this.mapPatient(item));
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingContent = false;
        this.recalculatePagination();
      }
    },

    mapPatient(item) {
      return {
        id: item.Id,
        name: item.Name,
        registerNumber: item.RegisterNumber || "",
        gender: item.Gender || "",
        age: this.formatAge(item.BirthDate),
        cid: this.$Utilities.getPatientCid(item) || "",
        team: this.resolveTeamName(item),
        // TODO: usar o campo real de licenciamento quando o backend expuser.
        isLicensed: Boolean(
          this.$Utilities.getValueIgnoreCase(item, "IsLicensed") ??
            this.$Utilities.getValueIgnoreCase(item, "Licensed")
        ),
        updatingLicense: false
      };
    },

    // Resolve o nome da equipe do paciente tolerando os formatos que o
    // backend pode retornar na listagem. No Lila.API a equipe vem dentro de
    // "Doctor" (Doctor com IsTeam=true); também aceita objeto Team {Name/name},
    // string, campo TeamName, ou apenas TeamId (resolvido via lista de equipes).
    resolveTeamName(item) {
      const doctor = this.$Utilities.getValueIgnoreCase(item, "Doctor");
      if (doctor && typeof doctor === "object") {
        const name = this.$Utilities.getValueIgnoreCase(doctor, "Name");
        if (name) return name;
      }

      const team = this.$Utilities.getValueIgnoreCase(item, "Team");

      if (team && typeof team === "object") {
        const name = this.$Utilities.getValueIgnoreCase(team, "Name");
        if (name) return name;
      }
      if (typeof team === "string" && team) return team;

      const teamName = this.$Utilities.getValueIgnoreCase(item, "TeamName");
      if (teamName) return teamName;

      const teamId =
        this.$Utilities.getValueIgnoreCase(item, "TeamId") ??
        (team && typeof team === "object"
          ? this.$Utilities.getValueIgnoreCase(team, "Id")
          : null);

      if (teamId != null) {
        const found = this.teams.find(
          (t) => String(t.id) === String(teamId)
        );
        if (found) return found.name;
      }

      return "";
    },

    parseBirthDate(birthDate) {
      const formats = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"];

      for (const format of formats) {
        const parsed = this.$moment(birthDate, format);
        if (parsed.isValid()) return parsed;
      }

      const iso = this.$moment(birthDate, this.$moment.ISO_8601);
      return iso.isValid() ? iso : null;
    },

    formatAge(birthDate) {
      if (!birthDate) return "";
      const parsed = this.parseBirthDate(birthDate);
      if (!parsed) return "—";

      const now = this.$moment();
      const years = now.diff(parsed, "years");
      if (years >= 1) return `${years} ${years === 1 ? "ano" : "anos"}`;

      const months = now.diff(parsed, "months");
      return `${months} ${months === 1 ? "mês" : "meses"}`;
    },

    async toggleLicense(row, event) {
      const newValue = event.target.checked;
      const previous = row.isLicensed;

      row.isLicensed = newValue;
      row.updatingLicense = true;
      this.applyLicenseDelta(newValue ? -1 : 1);

      try {
        await this.persistLicense(row, newValue);
      } catch (error) {
        row.isLicensed = previous;
        this.applyLicenseDelta(newValue ? 1 : -1);
        this.handleError(error);
      } finally {
        row.updatingLicense = false;
      }
    },

    async persistLicense(row, isLicensed) {
      await this.$axios.put(
        `api/Patient/${row.id}/license`,
        { IsLicensed: isLicensed },
        { headers: this.authHeader() }
      );
    },

    applyLicenseDelta(delta) {
      const next = this.licenses.available + delta;
      this.licenses.available = Math.max(0, Math.min(next, this.licenses.total));
    },

    async getLicenses() {
      // Licenças da instituição do profissional logado.
      try {
        const res = await this.$axios.get("api/Licenses", {
          headers: this.authHeader()
        });
        const data = res.data || {};
        this.licenses.unlimited = Boolean(
          this.$Utilities.getValueIgnoreCase(data, "Unlimited")
        );
        if (this.licenses.unlimited) {
          // Sem teto de licenças: a resposta omite os números — não tratar como zero.
          this.licenses.available = Infinity;
          this.licenses.total = Infinity;
          return;
        }
        this.licenses.available = Number(
          this.$Utilities.getValueIgnoreCase(data, "AvailableLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Available") ??
            0
        );
        this.licenses.total = Number(
          this.$Utilities.getValueIgnoreCase(data, "TotalLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Total") ??
            0
        );
      } catch (error) {
        console.error("Erro ao carregar licenças:", error);
      }
    },

    editRow(row) {
      this.$router.push(`/pacientes/ficha/${row.id}`);
    },

    // ---- Seleção em lote (bulk-edit) ----------------------------------
    isSelected(id) {
      return this.selectedIds.includes(id);
    },

    toggleSelect(row) {
      const index = this.selectedIds.indexOf(row.id);
      if (index === -1) this.selectedIds.push(row.id);
      else this.selectedIds.splice(index, 1);
    },

    toggleSelectAllPage(event) {
      const pageIds = this.pagedRows.map((row) => row.id);
      if (event.target.checked) {
        pageIds.forEach((id) => {
          if (!this.selectedIds.includes(id)) this.selectedIds.push(id);
        });
      } else {
        this.selectedIds = this.selectedIds.filter(
          (id) => !pageIds.includes(id)
        );
      }
    },

    clearSelection() {
      this.selectedIds = [];
    },

    // Licencia (true) ou remove a licença (false) dos selecionados.
    // Não há endpoint em lote — reaproveita o persistLicense por linha,
    // respeitando o total de licenças disponíveis ao licenciar.
    async applyBulkLicense(licensed) {
      if (this.bulkSaving) return;

      // Apenas as linhas que realmente mudam de estado.
      let targets = this.patients.filter(
        (row) => this.selectedIds.includes(row.id) && row.isLicensed !== licensed
      );
      if (targets.length === 0) {
        this.clearSelection();
        return;
      }

      // Ao licenciar, limita à quantidade de licenças disponíveis.
      let skippedNoLicense = 0;
      if (licensed && this.licenses.total > 0) {
        if (targets.length > this.licenses.available) {
          skippedNoLicense = targets.length - this.licenses.available;
          targets = targets.slice(0, this.licenses.available);
        }
        if (targets.length === 0) {
          this.$Utilities.controlAlert(
            null,
            this.alert,
            "warning",
            "Não há licenças disponíveis suficientes."
          );
          return;
        }
      }

      this.bulkSaving = true;
      let ok = 0;
      let fail = 0;
      for (const row of targets) {
        const previous = row.isLicensed;
        row.isLicensed = licensed;
        row.updatingLicense = true;
        this.applyLicenseDelta(licensed ? -1 : 1);
        try {
          await this.persistLicense(row, licensed);
          ok++;
        } catch (error) {
          row.isLicensed = previous;
          this.applyLicenseDelta(licensed ? 1 : -1);
          fail++;
        } finally {
          row.updatingLicense = false;
        }
      }
      this.bulkSaving = false;
      this.clearSelection();

      this.notifyBulkResult(licensed, ok, fail, skippedNoLicense);
    },

    notifyBulkResult(licensed, ok, fail, skippedNoLicense) {
      const acao = licensed ? "licenciado(s)" : "com licença removida";
      if (fail === 0 && skippedNoLicense === 0) {
        this.$Utilities.controlAlert(
          null,
          this.alert,
          "success",
          `${ok} paciente(s) ${acao}.`
        );
        return;
      }
      const partes = [`${ok} ${acao}`];
      if (fail) partes.push(`${fail} com erro`);
      if (skippedNoLicense) {
        partes.push(`${skippedNoLicense} sem licença disponível`);
      }
      this.$Utilities.controlAlert(
        null,
        this.alert,
        "warning",
        `${partes.join("; ")}.`
      );
    },

    contactSales() {
      this.$Utilities.controlAlert(
        null,
        this.alert,
        "success",
        "Para adquirir mais licenças, entre em contato com o suporte da Lila Saúde."
      );
    },

    applySearch() {
      this.filters.searchActive = this.filters.search.trim() !== "";
      this.pagination.current = 1;
    },

    clearSearch() {
      this.filters.search = "";
      this.filters.searchActive = false;
      this.pagination.current = 1;
    },

    applyClientFilters() {
      this.pagination.current = 1;
    },

    changePage(page) {
      this.pagination.current = page;
    },

    recalculatePagination() {
      const total = Math.ceil(this.filteredRows.length / this.pagination.perPage);
      this.pagination.totalPages = total;
      if (this.pagination.current > total) {
        this.pagination.current = total || 1;
      }
      if (this.pagination.current < 1) {
        this.pagination.current = 1;
      }
    },

    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    },

    handleError(error) {
      const data = error && error.response ? error.response.data : null;
      if (error && error.response) {
        this.$Utilities.verifyToken(error.response.status);
      }
      this.$Utilities.controlAlert(
        data,
        this.alert,
        "danger",
        "Ocorreu um erro ao carregar as informações."
      );
    }
  }
};
</script>
